import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { api, resolveMedia } from "../lib/api";
import { useLang } from "../i18n";
import { GALLERY_CATEGORIES } from "../data/content";
import { Reveal } from "../components/Reveal";

export default function Gallery() {
  const { lang, pick } = useLang();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState(GALLERY_CATEGORIES);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => {
      const list = r.data.items || [];
      if (list.length) {
        setCats([{ key: "all", hi: "सभी", en: "All" }, ...list.map((c) => ({ key: c.slug, hi: c.name_hi, en: c.name_en || c.name_hi }))]);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/gallery${cat === "all" ? "" : `?category=${cat}`}`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cat]);

  const caption = (it) => (lang === "hi" ? it.caption_hi : it.caption_en || it.caption_hi);
  const move = (dir) => {
    if (lightbox == null) return;
    setLightbox((lightbox + dir + items.length) % items.length);
  };

  return (
    <main>
      <Seo
        title="फोटो गैलरी — हमारी गतिविधियों की तस्वीरें"
        description="Anansha Welfare Society की असली गतिविधि तस्वीरें — शिक्षा कार्यक्रम, स्वास्थ्य शिविर, सिलाई प्रशिक्षण, वृक्षारोपण और सामुदायिक जागरूकता, वाराणसी।"
        path="/gallery"
        breadcrumb={["गैलरी"]}
      />
      <PageHero
        overline={{ hi: "तस्वीरें", en: "Photos" }}
        title={{ hi: "फोटो गैलरी", en: "Photo Gallery" }}
        description={{ hi: "हमारी वास्तविक गतिविधियों और कार्यक्रमों की झलकियां।", en: "Glimpses of our real activities and programs." }}
        testid="gallery-page-heading"
      />

      <section className="py-16 bg-ngo-cream min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div data-testid="gallery-filters" className="flex flex-wrap gap-2.5 mb-12">
            {cats.map((c) => (
              <button
                key={c.key}
                data-testid={`filter-${c.key}`}
                onClick={() => setCat(c.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                  cat === c.key ? "bg-ngo-deep text-white" : "bg-white text-ngo-deep border border-ngo-deep/20 hover:bg-ngo-mint"
                }`}
              >
                {lang === "hi" ? c.hi : c.en}
              </button>
            ))}
          </div>

          {loading ? (
            <p data-testid="gallery-loading" className="text-center text-ngo-ink/50 py-20">...</p>
          ) : items.length === 0 ? (
            <p data-testid="gallery-empty" className="text-center text-ngo-ink/50 py-20">
              {pick({ hi: "इस श्रेणी में अभी फोटो उपलब्ध नहीं हैं।", en: "No photos available in this category yet." })}
            </p>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
              {items.map((item, i) => (
                <Reveal key={item.id || i} i={i % 3} className="mb-5 break-inside-avoid">
                  <button
                    data-testid={`gallery-item-${i}`}
                    onClick={() => setLightbox(i)}
                    className="group relative w-full rounded-2xl overflow-hidden border border-ngo-deep/10 shadow-[0_8px_30px_rgba(23,107,58,0.08)] text-left"
                  >
                    <img src={resolveMedia(item.url)} alt={`${item.caption_hi} — Anansha Welfare Society, Varanasi`} title={item.caption_hi} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ngo-forest/90 to-transparent px-5 pt-12 pb-4 text-sm font-medium text-ngo-cream">
                      {caption(item)}
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox != null && items[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ngo-forest/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
          >
            <button data-testid="lightbox-close" aria-label="बंद करें" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-200" onClick={() => setLightbox(null)}>
              <X size={22} />
            </button>
            <button data-testid="lightbox-prev" aria-label="पिछली फोटो" className="absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-200" onClick={(e) => { e.stopPropagation(); move(-1); }}>
              <ChevronLeft size={22} />
            </button>
            <button data-testid="lightbox-next" aria-label="अगली फोटो" className="absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-200" onClick={(e) => { e.stopPropagation(); move(1); }}>
              <ChevronRight size={22} />
            </button>
            <motion.figure
              key={lightbox}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={resolveMedia(items[lightbox].url)} alt={items[lightbox].caption_hi} className="w-full max-h-[76vh] object-contain rounded-2xl" />
              <figcaption className="mt-4 text-center text-ngo-mint/90 text-sm sm:text-base">{caption(items[lightbox])}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
